import { Text } from '@thanx/uikit/text'
import { useGetUserListCustomersQuery } from 'api/userLists'
import InfoBanner from 'components/banners/InfoBanner'
import HeaderCell from 'components/HeaderCell'
import Pagination from 'components/Pagination'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap-five'
import { Link } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { customerProfileUrl } from 'utilities/urlUtils'

const t = buildTranslate('user_lists.customers')
const SEARCH_DEBOUNCE_MS = 300
const PER_PAGE = 20

function formatCustomerName(customer: {
  first_name?: string
  last_name?: string
  email?: string
}): string {
  if (customer.first_name || customer.last_name) {
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
  }
  return customer.email || t('anonymous')
}

type Props = {
  listId: number
}

const CustomersTab: React.FC<Props> = ({ listId }) => {
  const [searchInput, setSearchInput] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const [debouncedSetQuery, cancelDebounce] = useDebouncedCallback(
    (value: string) => {
      setDebouncedQuery(value)
      setCurrentPage(1)
    },
    SEARCH_DEBOUNCE_MS
  )

  // Reset state when navigating between lists
  useEffect(() => {
    setCurrentPage(1)
    setSearchInput('')
    setDebouncedQuery('')
    cancelDebounce()
  }, [listId, cancelDebounce])

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
    debouncedSetQuery(e.target.value)
  }

  const { data, isLoading } = useGetUserListCustomersQuery(
    {
      userListId: listId,
      query: debouncedQuery || undefined,
      page: currentPage,
      per_page: PER_PAGE,
    },
    { refetchOnMountOrArgChange: true }
  )

  const customers = data?.data || []
  const totalCount = data?.pagination?.total_count || 0
  const totalPages = data?.pagination?.total_pages || 0

  // Auto-reset to page 1 if current page returns empty (e.g., data changed)
  useEffect(() => {
    if (currentPage > 1 && customers.length === 0 && !isLoading) {
      setCurrentPage(1)
    }
  }, [currentPage, customers.length, isLoading])

  return (
    <div className="container py-l">
      <Spinner isLoading={isLoading} className="text-center py-xl">
        <div className="mb-m">
          <InfoBanner text={t('replication_notice')} showIcon />
        </div>
        {customers.length === 0 && !searchInput ? (
          <div className="text-center py-xl">
            <Text.BodyText3 color="grey50" tag="p" className="m-0 font-italic">
              {t('empty')}
            </Text.BodyText3>
          </div>
        ) : (
          <>
            <div className="mb-l">
              <Text.Header2 className="m-0">
                {totalCount.toLocaleString()}
              </Text.Header2>
              <Text.BodyText4 className="m-0 grey-70">
                {t('total_label')}
              </Text.BodyText4>
            </div>
            <input
              className="form-control mb-l"
              placeholder={t('search_placeholder')}
              value={searchInput}
              onChange={handleSearch}
            />
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <HeaderCell>{t('columns.customer')}</HeaderCell>
                  <HeaderCell />
                </tr>
              </thead>
              <tbody>
                {customers.map(customer => (
                  <tr key={customer.id}>
                    <td className="body-text-4 py-s px-m">
                      {formatCustomerName(customer)}
                    </td>
                    <td className="body-text-4 py-s px-m text-right">
                      <Link to={customerProfileUrl(customer.uid)}>
                        {t('view_profile')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-m">
                <Pagination
                  currentPage={currentPage}
                  numPages={totalPages}
                  onChangePage={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </Spinner>
    </div>
  )
}

export default CustomersTab
